// TODO: Move to context-menu slice
import { Canvas, ActiveSelection } from 'fabric';

// import { OFFSET_Y_MENU } from '../config/constants';

import { MenuItem, MenuType } from '../types/types';
import { Marker } from '../metaBullets/classes/Marker';
import { TextEntity } from '../metaBullets/classes/TextEntity';

import { BULLET, CANVAS_CONTEXT_MENU_FIXED_POSITION } from '../config/constants';

import { isObjectLocked } from './lockObjects';
import { findObjectById } from './findObjects';
import { isBullet, isBulletTextItem } from './groupUtils';
import { isMarker, isSelectionOnlyMarkers } from './metaBullets/markerSelectionUtils';
import { isTextEntityWithMarkerImage, isMarkerWithImage } from './metaBullets/newBulletIconUtils';

export const calculatePositionMenu = (
  origin: number | string | undefined,
  value: number,
  dimension: number,
  scale: number,
  offset = 0,
  objectScale = 1,
) => {
  const scaledDimension = dimension * objectScale;

  // Улучшенный расчет отступа с ограничением для малых масштабов
  let scaledOffset;

  if (offset === 0) {
    // Для горизонтального позиционирования (x) отступ не применяем
    scaledOffset = 0;
  } else {
    // Для вертикального позиционирования (y)
    // Ограничиваем минимальный масштаб до 0.9 для отступа, чтобы избежать слишком большого отступа на малых масштабах
    const effectiveScale = Math.max(scale, 0.9);
    // Фиксированный базовый отступ (независимо от масштаба)
    const baseOffset = 40;
    // Добавляем компонент, зависящий от масштаба (увеличивается при увеличении масштаба)
    const scaleComponent = (offset - baseOffset) / effectiveScale;

    scaledOffset = baseOffset + scaleComponent;
  }

  if (origin === 'center') {
    // For objects with a central point of attachment
    return value * scale - (scaledDimension * scale) / 2 - scaledOffset;
  } else if (origin === 'left' || origin === 'top') {
    // For objects with a left/top attachment
    return value * scale - scaledOffset;
  } else if (origin === 'right' || origin === 'bottom') {
    // For objects with a right/bottom attachment
    return (value - scaledDimension) * scale - scaledOffset;
  }

  // By default, consider as center
  return value * scale - (scaledDimension * scale) / 2 - scaledOffset;
};

/**
 * Generates menu items for a specific object type.
 * @param params Parameters for generating menu items.
 * @param params.objectType The type of the object ('textbox', 'group', 'rectangle', etc.).
 * @param params.targetIds The IDs of the target objects.
 * @param params.canvas The Canvas instance.
 * @param params.canvasId The Canvas identifier.
 * @returns A list of menu items.
 */
export const getMenuItemsByObjectType = ({
  objectType,
  targetIds,
  canvas,
  canvasId,
  isDevMode = false,
}: {
  objectType: string; // The type of the object
  targetIds: string[]; // The IDs of the target objects
  canvas: Canvas; // The Canvas instance
  canvasId: string; // The Canvas identifier
  isDevMode?: boolean;
}): MenuItem[] => {
  let typesMenu: MenuType[] = []; // A list of menu types

  // получаем объект по его ID
  const activeObject =
    targetIds.length === 1 ? findObjectById(canvas.getObjects(), targetIds[0]) : null;

  const isMoreThanOneObject = targetIds.length > 1;

  // проверяем, заблокирован ли объект
  const isLocked = activeObject ? isObjectLocked(activeObject) : false;

  // если объект заблокирован, показываем только опцию разблокировки
  if (isLocked) {
    typesMenu = ['lockObject']; // Только опция разблокировки
    return typesMenu.map((type) => ({
      item: { type },
      targetIds,
      canvas,
      canvasId,
    }));
  }

  if (isDevMode) {
    typesMenu = typesMenu.concat(['devMode']);
  }

  // Проверяем, является ли выделение группой объектов (ActiveSelection)
  const activeSelection = canvas.getActiveObject();
  const activeObjects = canvas.getActiveObjects();
  const isGroupSelection =
    targetIds.length > 1 || activeSelection instanceof ActiveSelection || activeObjects.length > 1;

  // Добавляем upLayer и downLayer для всех типов, кроме маркеров и групп объектов
  if (objectType !== 'marker' && objectType !== 'group-markers' && !isGroupSelection) {
    typesMenu = typesMenu.concat(['upLayer', 'downLayer']);
  }

  // Determine menu items based on the object type
  switch (objectType) {
    case 'textbox':
    case 'textentity': {
      // 🔢 БЛОКИРУЕМ entityType и editBullet для digital-accent (digit и superscript)
      // Нельзя менять тип сущности и редактировать буллит для digital-accent объектов
      let isDigitalAccent = false;
      if (activeObject && TextEntity.isTextEntity(activeObject)) {
        const textEntity = activeObject as TextEntity;
        // Проверяем через entityType (digital-accent или digital-accent-superscript)
        isDigitalAccent =
          textEntity.entityType === 'digital-accent' ||
          textEntity.entityType === 'digital-accent-superscript' ||
          (textEntity.isDigitalAccent && textEntity.isDigitalAccent());

        if (isDigitalAccent) {
          console.debug('🔢 Blocking entityType and editBullet menu for digital-accent:', {
            entityId: textEntity.id,
            entityType: textEntity.entityType,
          });
        }
      }

      const baseMenuItems: MenuType[] = [
        'convertToBullet',
        'textAlign',
        'fontStyle',
        'fontSize',
        'delete',
        'copy',
        'lockObject',
      ];

      // Добавляем entityType только если это НЕ digital-accent
      if (!isDigitalAccent) {
        baseMenuItems.unshift('entityType');
      }

      // Добавляем editBullet только если это НЕ digital-accent
      if (!isDigitalAccent) {
        baseMenuItems.splice(4, 0, 'editBullet'); // Вставляем после fontSize
      }

      typesMenu = typesMenu.concat(baseMenuItems);

      break;
    }
    case BULLET.GROUP:
      if (isMoreThanOneObject) {
        typesMenu = typesMenu.concat(['align', 'editBullet', 'delete', 'copy', 'lockObject']);
      } else {
        typesMenu = typesMenu.concat(['editBullet', 'delete', 'copy', 'lockObject']);
      }
      break;
    case BULLET.GROUP_WITHOUT_ICON:
      if (isMoreThanOneObject) {
        typesMenu = typesMenu.concat(['align', 'delete', 'copy', 'lockObject']);
      } else {
        typesMenu = typesMenu.concat(['delete', 'copy', 'lockObject']);
      }
      break;
    case BULLET.TEXTBOX:
      typesMenu = typesMenu.concat([
        'textAlign',
        'fontStyle',
        'fontSize',
        'editBullet',
        'delete',
        'lockObject',
      ]);
      break;
    case BULLET.TEXTBOX_WITHOUT_ICON:
      typesMenu = typesMenu.concat(['textAlign', 'fontStyle', 'fontSize', 'delete', 'lockObject']);
      break;
    case 'group':
      typesMenu = typesMenu.concat(['align', 'delete', 'copy', 'lockObject']);
      break;
    case 'group-text':
      typesMenu = typesMenu.concat([
        'align',
        'textAlign',
        'fontStyle',
        'fontSize',
        'delete',
        'copy',
        'lockObject',
      ]);
      break;
    case 'rectangle':
    case 'triangle':
    case 'circle':
      typesMenu = typesMenu.concat(['delete', 'copy', 'lockObject']);
      typesMenu.unshift('fillColor');
      break;
    case 'imageCrop':
      typesMenu = typesMenu.concat([
        'changeImage',
        'editImageCrop',
        'delete',
        'copy',
        'lockObject',
      ]);
      break;
    case 'image':
      typesMenu = typesMenu.concat(['changeImage', 'delete', 'copy', 'lockObject']);
      break;
    case 'path':
      typesMenu = typesMenu.concat(['delete', 'copy', 'lockObject']);
      typesMenu.unshift('fillColor');
      break;
    case 'textentity-with-marker-image': {
      // TextEntity с маркером-изображением (новый метабулит)
      // 🔢 БЛОКИРУЕМ entityType и editBullet для digital-accent (digit и superscript)
      let isDigitalAccentWithImage = false;
      if (activeObject && TextEntity.isTextEntity(activeObject)) {
        const textEntity = activeObject as TextEntity;
        // Проверяем через entityType (digital-accent или digital-accent-superscript)
        isDigitalAccentWithImage =
          textEntity.entityType === 'digital-accent' ||
          textEntity.entityType === 'digital-accent-superscript' ||
          (textEntity.isDigitalAccent && textEntity.isDigitalAccent());
      }

      const baseMenuItemsWithImage: MenuType[] = [
        'convertToBullet',
        'textAlign',
        'fontStyle',
        'fontSize',
        'delete',
        'copy',
        'lockObject',
      ];

      // Добавляем entityType только если это НЕ digital-accent
      if (!isDigitalAccentWithImage) {
        baseMenuItemsWithImage.unshift('entityType');
      }

      // Добавляем editBullet только если это НЕ digital-accent
      if (!isDigitalAccentWithImage) {
        baseMenuItemsWithImage.splice(4, 0, 'editBullet'); // Вставляем после fontSize
      }

      typesMenu = typesMenu.concat(baseMenuItemsWithImage);
      break;
    }
    case 'marker-with-image':
      // Маркер с изображением - показываем editBullet и удаление
      typesMenu = typesMenu.concat(['editBullet', 'delete']);
      break;
    case 'marker':
      // Одиночный маркер - показываем только удаление
      typesMenu = typesMenu.concat(['delete']);
      break;
    case 'group-markers':
      // Группа маркеров - показываем только удаление
      typesMenu = typesMenu.concat(['delete']);
      break;
    default:
      typesMenu = typesMenu.concat(['delete', 'copy', 'lockObject']);
      break;
  }

  const menuItems = typesMenu.map((type) => ({
    item: { type },
    targetIds,
    canvas,
    canvasId,
  }));

  return menuItems;
};

type UpdateContextMenuParams = {
  canvas: Canvas;
  canvasId: string;
  setMenuItems: (items: MenuItem[]) => void;
  setContextMenuPos: (pos: { x: number; y: number }) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buffer: { objects: any[] };
  resetMenu: () => void;
  isDevMode?: boolean;
};

/**
 * Проверяет, содержит ли выделение хотя бы одну TextEntity или Marker
 * @param objects - Массив выделенных объектов
 * @returns true если есть хотя бы одна TextEntity или Marker
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasTextEntityOrMarker = (objects: any[]): boolean => {
  return objects.some((obj) => {
    // Проверка на TextEntity
    if (TextEntity.isTextEntity(obj)) {
      return true;
    }
    // Проверка на Marker
    if (isMarker(obj)) {
      return true;
    }
    return false;
  });
};

/**
 * Фильтрует пункты меню, убирая align, lockObject, upLayer, downLayer
 * если в выделении есть TextEntity или Marker.
 * Также делает copy disabled, если это множественное выделение или редактирование текста.
 * @param menuItems - Исходные пункты меню
 * @param shouldFilter - Нужно ли фильтровать
 * @param isMultipleSelection - Множественное ли выделение
 * @param isEditingText - Редактируется ли текст внутри TextEntity/Textbox
 * @returns Отфильтрованные пункты меню
 */
const filterMenuItemsForTextEntityAndMarkers = (
  menuItems: MenuItem[],
  shouldFilter: boolean,
  isMultipleSelection: boolean,
  isEditingText: boolean = false,
): MenuItem[] => {
  if (!shouldFilter) {
    return menuItems;
  }

  // Всегда исключаем для TextEntity/Marker
  const alwaysForbiddenTypes = ['align', 'lockObject', 'upLayer', 'downLayer'];

  return menuItems
    .filter((menuItem) => {
      // Удаляем запрещенные типы
      return !alwaysForbiddenTypes.includes(menuItem.item.type);
    })
    .map((menuItem) => {
      // Для copy делаем disabled при:
      // 1. Множественном выделении с TextEntity/Marker
      // 2. Редактировании текста внутри одного TextEntity/Textbox (частичное выделение текста)
      if (menuItem.item.type === 'copy' && (isMultipleSelection || isEditingText)) {
        return {
          ...menuItem,
          item: {
            ...menuItem.item,
            disabled: true as const,
          },
        };
      }
      return menuItem;
    });
};

export const updateContextMenuUtility = ({
  canvas,
  canvasId,
  setMenuItems,
  setContextMenuPos,
  buffer,
  resetMenu,
  isDevMode = false,
}: UpdateContextMenuParams) => {
  setMenuItems([]);
  const activeObjects = canvas.getActiveObjects();
  // const zoom = canvas.getZoom();

  if (activeObjects && activeObjects.length > 0) {
    const activeGroupSelection = canvas.getActiveObject();
    if (!activeGroupSelection) {
      return;
    }

    // Проверяем наличие TextEntity или Marker в выделении
    const shouldFilterMenuItems = hasTextEntityOrMarker(activeObjects);
    const isMultipleSelection = activeObjects.length > 1;

    // Для одиночного выделения TextEntity/Marker - всегда блокируем copy
    // Не важно, редактируется текст или нет - метабулиты нельзя дублировать частично
    const isEditingText = !isMultipleSelection && shouldFilterMenuItems;

    // Обертка для setMenuItems с фильтрацией
    const setFilteredMenuItems = (items: MenuItem[]) => {
      const filteredItems = filterMenuItemsForTextEntityAndMarkers(
        items,
        shouldFilterMenuItems,
        isMultipleSelection,
        isEditingText,
      );
      setMenuItems(filteredItems);
    };

    // Проверяем маркеры
    const isOnlyMarkers = isSelectionOnlyMarkers(activeGroupSelection);
    if (isOnlyMarkers) {
      console.debug('🎯 Context menu for markers:', {
        isGroup: activeObjects.length > 1,
        markersCount: activeObjects.length,
      });

      // Для одиночного маркера проверяем есть ли изображение
      let objectType = activeObjects.length > 1 ? 'group-markers' : 'marker';

      if (activeObjects.length === 1) {
        const singleMarker = activeObjects[0];
        const hasImage = isMarkerWithImage(canvas, singleMarker);

        console.debug('🎯 Single marker - checking for image:', {
          markerId: singleMarker.id,
          hasImage,
        });

        objectType = hasImage ? 'marker-with-image' : 'marker';
      }

      setFilteredMenuItems(
        getMenuItemsByObjectType({
          objectType,
          targetIds: activeObjects.map((item) => item.id),
          canvas,
          canvasId,
          isDevMode,
        }),
      );
      setContextMenuPos(CANVAS_CONTEXT_MENU_FIXED_POSITION);
      return;
    }

    const isTextInBullet = isBulletTextItem(activeGroupSelection);

    const isGroupBullet =
      activeGroupSelection.type.toLowerCase() === 'group' && isBullet(activeGroupSelection);

    const isIncludeTextsObjects = activeObjects.every(
      (obj) => obj.type.toLowerCase() === 'textbox' || obj.type.toLowerCase() === 'textentity',
    );

    const isGroupBullets = activeObjects.every((obj) => isBullet(obj));

    // Determine target object for positioning
    const targetObject = isTextInBullet
      ? canvas.getObjects().find((obj) => obj.id === activeGroupSelection.idGroup) ||
        activeGroupSelection
      : activeGroupSelection;

    const isBulletWithoutIcon = targetObject.isBulletWithoutIcon ?? false;

    // Calc x ()
    // TODO: Now comment, because menu will be fixed, but in future we can use this
    // const x = calculatePositionMenu(
    //   targetObject.originX || 'center',
    //   targetObject.left,
    //   targetObject.width,
    //   zoom,
    //   0,
    //   targetObject.scaleX || 1,
    // );

    // Calc y
    // const y = calculatePositionMenu(
    //   targetObject.originY || 'center',
    //   targetObject.top,
    //   targetObject.height,
    //   zoom,
    //   OFFSET_Y_MENU,
    //   targetObject.scaleY || 1,
    // );

    if (activeObjects.length > 1) {
      if (isGroupBullets) {
        setFilteredMenuItems(
          getMenuItemsByObjectType({
            objectType: activeObjects.some((item) => item.isBulletWithoutIcon)
              ? BULLET.GROUP_WITHOUT_ICON
              : BULLET.GROUP,
            targetIds: activeObjects.map((item) => item.id),
            canvas,
            canvasId,
            isDevMode,
          }),
        );
      } else if (isIncludeTextsObjects) {
        setFilteredMenuItems(
          getMenuItemsByObjectType({
            objectType: 'group-text',
            targetIds: activeObjects.map((item) => item.id),
            canvas,
            canvasId,
            isDevMode,
          }),
        );
      } else {
        setFilteredMenuItems(
          getMenuItemsByObjectType({
            objectType: 'group',
            targetIds: activeObjects.map((item) => item.id),
            canvas,
            canvasId,
            isDevMode,
          }),
        );
      }
    } else {
      const activeObject = activeObjects[0]; //Chosen 1 element

      // Проверяем одиночный маркер

      if (isMarker(activeObject)) {
        const marker = activeObject as Marker;
        const hasImage = isMarkerWithImage(canvas, activeObject);

        console.debug('🎯 Context menu for single marker:', {
          markerId: marker.id,
          entityId: marker.entityId,
          entityType: marker.entityType,
          hasImage,
        });

        setFilteredMenuItems(
          getMenuItemsByObjectType({
            objectType: hasImage ? 'marker-with-image' : 'marker',
            targetIds: [activeObject.id],
            canvas,
            canvasId,
            isDevMode,
          }),
        );
        setContextMenuPos(CANVAS_CONTEXT_MENU_FIXED_POSITION);
        return;
      }

      switch (activeObject.type?.toLocaleLowerCase()) {
        case 'textbox':
        case 'textentity':
          if (isBulletTextItem(activeObject)) {
            setFilteredMenuItems(
              getMenuItemsByObjectType({
                objectType: isBulletWithoutIcon ? BULLET.TEXTBOX_WITHOUT_ICON : BULLET.TEXTBOX,
                targetIds: [activeObject.id],
                canvas,
                canvasId,
                isDevMode,
              }),
            );
          } else if (isTextEntityWithMarkerImage(canvas, activeObject)) {
            // Новый метабулит с маркером-изображением
            setFilteredMenuItems(
              getMenuItemsByObjectType({
                objectType: 'textentity-with-marker-image',
                targetIds: [activeObject.id],
                canvas,
                canvasId,
                isDevMode,
              }),
            );
          } else {
            // Для новых MetaBullet TextEntity без маркера-изображения показываем editBullet (иконка может быть недоступна)
            setFilteredMenuItems(
              getMenuItemsByObjectType({
                objectType: 'textentity',
                targetIds: [activeObject.id],
                canvas,
                canvasId,
                isDevMode,
              }),
            );
          }
          break;
        case 'circle':
        case 'rectangle':
          setFilteredMenuItems(
            getMenuItemsByObjectType({
              objectType: activeObject.type,
              targetIds: [activeObject.id],
              canvas,
              canvasId,
              isDevMode,
            }),
          );
          break;
        case 'image':
          if (activeObject.category?.toLocaleLowerCase() === 'crop') {
            setFilteredMenuItems(
              getMenuItemsByObjectType({
                objectType: 'imageCrop',
                targetIds: [activeObject.id],
                canvas,
                canvasId,
                isDevMode,
              }),
            );
          } else if (
            activeObject?.media_type?.toLowerCase() === 'photo' ||
            activeObject?.media_type?.toLowerCase() === 'object'
          ) {
            setFilteredMenuItems(
              getMenuItemsByObjectType({
                objectType: 'image',
                targetIds: [activeObject.id],
                canvas,
                canvasId,
                isDevMode,
              }),
            );
          } else {
            //FIXME: надо спросить у бека, пока бывают кейсы, когда imageCrop не является image
            setFilteredMenuItems(
              getMenuItemsByObjectType({
                objectType: 'image',
                targetIds: [activeObject.id],
                canvas,
                canvasId,
                isDevMode,
              }),
            );
          }
          break;
        case 'group':
          if (isGroupBullet) {
            setFilteredMenuItems(
              getMenuItemsByObjectType({
                objectType: isBulletWithoutIcon ? BULLET.GROUP_WITHOUT_ICON : BULLET.GROUP,
                targetIds: [activeObject.id],
                canvas,
                canvasId,
                isDevMode,
              }),
            );
          }
          break;
        case 'path':
          setFilteredMenuItems(
            getMenuItemsByObjectType({
              objectType: 'path',
              targetIds: [activeObject.id],
              canvas,
              canvasId,
              isDevMode,
            }),
          );
          break;
        default:
          // Для объектов с неопределенным типом все равно показываем меню с deleteAndCopy
          setFilteredMenuItems(
            getMenuItemsByObjectType({
              objectType: 'unknown',
              targetIds: [activeObject.id],
              canvas,
              canvasId,
              isDevMode,
            }),
          );
          break;
      }
    }
    // if (x && y) {
    setContextMenuPos(CANVAS_CONTEXT_MENU_FIXED_POSITION);
    // }
  } else {
    if (buffer.objects.length) {
      setMenuItems([
        {
          item: { type: 'paste' },
          targetIds: [],
          canvas,
          canvasId,
          closeOnClick: true,
        },
      ]);
    } else {
      resetMenu();
    }
  }
};
